'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocale, useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { sendGAEvent } from '@next/third-parties/google';
import { autoGrowTextarea } from '@/lib/autoGrow';
import { useFocusTrap } from '@/lib/useFocusTrap';
import Button from './Button';

type FormData = {
  name: string;
  phone: string;
  comment: string;
  messenger: 'Telegram' | 'WhatsApp' | 'Phone call';
};

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}

function ModalContent({ open, onClose }: ContactModalProps) {
  const t = useTranslations('modal');
  const tForm = useTranslations('form');
  const locale = useLocale();
  const [submitted, setSubmitted] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  // Honeypot kept OUT of react-hook-form so it never appears in validated data.
  const [honeypot, setHoneypot] = useState('');
  // Timestamp the form was opened — used by the server's timing check.
  const startedAtRef = useRef(Date.now());
  // Fires the GA `form_start` event once per modal-open, on the first real edit.
  const formStartedRef = useRef(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Built here so validation messages are translated per locale.
  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, tForm('errorInputRequired'))
          .max(191, tForm('errorNameTooLong')),
        phone: z
          .string()
          .trim()
          .min(1, tForm('errorInputRequired'))
          .max(20, tForm('errorPhoneTooLong'))
          .regex(/^[0-9\s\+\-\(\)]+$/, tForm('errorPhoneFormat'))
          .refine((val) => {
            if (!val) return true;
            const digitCount = (val.match(/\d/g) || []).length;
            return digitCount >= 9 && digitCount <= 15;
          }, tForm('errorPhoneFormat')),
        messenger: z.enum(['Telegram', 'WhatsApp', 'Phone call']),
        comment: z.string().max(191, tForm('errorCommentTooLong')).optional(),
      }),
    [tForm]
  );

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    // Validate only on submit, then re-validate on change so a shown error clears
    // as soon as the field is fixed. Crucially, focusing/blurring a field never
    // triggers validation — so clicking away from the auto-focused name field
    // can't flash a premature "required" error.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { messenger: 'WhatsApp', comment: '' },
  });

  const selectedMessenger = watch('messenger');
  const comment = watch('comment') ?? '';

  // Register the name field once and merge RHF's ref with ours so we can focus
  // it programmatically (see the open effect below).
  const { ref: nameFieldRef, ...nameField } = register('name');

  useEffect(() => {
    if (!open) return;
    // Reset the form each time the modal opens.
    setSubmitted(false);
    setShowComment(false);
    setSubmitError(false);
    setIsSubmitting(false);
    setHoneypot('');
    startedAtRef.current = Date.now();
    formStartedRef.current = false;
    reset();

    // Robust scroll lock: pin <body> in place and restore the scroll position on
    // close. `overflow: hidden` alone does NOT stop iOS Safari from scrolling the
    // page behind the modal — fixing the body does.
    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    // Blurs the page content behind the modal (see body.modal-open in globals.css).
    body.classList.add('modal-open');
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.classList.remove('modal-open');
      // `behavior: 'instant'` overrides the global `scroll-behavior: smooth`
      // (globals.css) so the page is restored to its exact spot without
      // animating down from the top after the modal closes.
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    };
  }, [open, reset]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus trap + restore for the dialog. Initial focus goes to the name field on
  // fine-pointer (mouse) devices, but to the dialog container on touch — focusing
  // an input there pops the on-screen keyboard, shifts the layout, and makes the
  // first tap on the close button miss.
  const initialFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const finePointer =
      typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;
    initialFocusRef.current = finePointer ? nameInputRef.current : dialogRef.current;
  }, [open]);
  useFocusTrap(open, dialogRef, initialFocusRef);

  if (!open) return null;

  // GA: first genuine edit of any field = the user started the form. Fires once
  // per open (focus alone doesn't count — the name field auto-focuses on open).
  const handleFirstInteraction = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    sendGAEvent('event', 'form_start', { form_type: 'quick_contact' });
  };

  const onSubmit = async (values: FormData) => {
    setSubmitError(false);
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'contact',
          name: values.name,
          phone: values.phone,
          comment: values.comment || '',
          messenger: values.messenger,
          locale,
          company: honeypot,
          startedAt: startedAtRef.current,
        }),
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.ok) {
        setSubmitted(true);
        // GA conversion: a quick-contact lead was captured. No PII — only the
        // form type, chosen messenger, and locale.
        sendGAEvent('event', 'generate_lead', {
          form_type: 'quick_contact',
          messenger: values.messenger,
          locale,
        });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      data-modal-root
      className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain outline-none"
      role="dialog"
      aria-modal="true"
    >
      <div
        // Dim scrim. The blur itself is applied to the page content via
        // body.modal-open (globals.css), not backdrop-filter here — iOS Safari
        // drops backdrop-filter while <body> is position:fixed (our scroll lock),
        // so it wouldn't blur on mobile. Fixed (not absolute) so it keeps covering
        // the viewport while the panel scrolls when it's taller than the screen.
        className="fixed inset-0 bg-background/40"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Centres the panel when it fits; when the panel is taller than the
          viewport this wrapper grows and the dialog scrolls so the submit button
          stays reachable. pointer-events-none lets clicks on the empty area fall
          through to the backdrop (→ close); the panel re-enables its own. */}
      <div className="pointer-events-none relative flex min-h-full items-center justify-center p-4">
        <div className="pointer-events-auto relative z-10 w-full max-w-md forest-card p-6 sm:p-8">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-on-surface-variant hover:text-on-surface transition-colors"
          aria-label={t('close')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round"/>
          </svg>
        </button>

        {submitted ? (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary-container flex items-center justify-center mx-auto">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="font-headline text-headline-lg text-on-surface">
              {tForm('successTitle')}
            </h3>
            <p className="text-on-surface-variant">
              {tForm('successDesc')}
            </p>
          </div>
        ) : (
          <>
            <h2 className="font-headline text-headline-lg text-on-surface mb-6">
              {t('title')}
            </h2>
            <form onSubmit={handleSubmit(onSubmit)} onChange={handleFirstInteraction}>
              {/* Lock every control while the request is in flight: disables the
                  name, phone, comment, and preferred-messenger inputs (and the
                  submit button) so nothing can change mid-submit. */}
              <fieldset disabled={isSubmitting} className="m-0 min-w-0 space-y-5 border-0 p-0">
              {/* Honeypot: hidden from humans, irresistible to bots. A filled
                  value makes the server drop the submission silently. */}
              <input
                type="text"
                name="company"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute left-[-9999px] h-0 w-0 opacity-0"
              />
              <div>
                <label htmlFor="modal-name" className="block text-label-sm font-semibold text-on-surface mb-2">
                  {tForm('step4Label')}
                </label>
                <input
                  id="modal-name"
                  {...nameField}
                  ref={(el) => {
                    nameFieldRef(el);
                    nameInputRef.current = el;
                  }}
                  placeholder={tForm('step4Placeholder')}
                  className={`w-full bg-transparent border-b outline-none text-on-surface py-4 text-body-lg placeholder:text-outline transition-colors ${
                    errors.name ? 'border-error' : 'border-outline-variant focus:border-primary'
                  }`}
                />
                {/* Reserved error row — always present so the panel height is
                    stable and showing an error never shifts the layout. */}
                <p className="text-error text-xs font-medium mt-2 min-h-[1rem]">
                  {errors.name?.message ?? ' '}
                </p>
              </div>
              <div>
                <label htmlFor="modal-phone" className="block text-label-sm font-semibold text-on-surface mb-2">
                  {tForm('step5Label')}
                </label>
                <input
                  id="modal-phone"
                  {...register('phone')}
                  type="tel"
                  placeholder={tForm('step5Placeholder')}
                  className={`w-full bg-transparent border-b outline-none text-on-surface py-4 text-body-lg placeholder:text-outline transition-colors ${
                    errors.phone ? 'border-error' : 'border-outline-variant focus:border-primary'
                  }`}
                />
                {/* Reserved error row — always present so the panel height is
                    stable and showing an error never shifts the layout. */}
                <p className="text-error text-xs font-medium mt-2 min-h-[1rem]">
                  {errors.phone?.message ?? ' '}
                </p>
              </div>
              {/* Optional job description — collapsed by default; click to reveal. */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowComment((v) => !v)}
                  aria-expanded={showComment}
                  aria-controls="modal-comment"
                  className="flex items-center gap-2 text-label-sm font-semibold text-on-surface-variant transition-colors hover:text-on-surface"
                >
                  <span
                    aria-hidden
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full border border-outline-variant text-primary transition-transform duration-200 ${showComment ? 'rotate-45' : ''}`}
                  >
                    +
                  </span>
                  <span>{tForm('commentLabel')}</span>
                  <span className="text-xs font-normal text-outline">({tForm('commentOptional')})</span>
                </button>

                {showComment && (
                  <div className="mt-3">
                    <div className="overflow-hidden rounded-lg border border-outline-variant bg-surface-container transition-colors focus-within:border-primary">
                      <textarea
                        id="modal-comment"
                        {...register('comment')}
                        maxLength={191}
                        rows={3}
                        autoFocus
                        onInput={autoGrowTextarea}
                        placeholder={tForm('commentPlaceholder')}
                        className="block w-full resize-none overflow-hidden bg-transparent px-4 py-3 text-body-md text-on-surface outline-none placeholder:text-outline"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      {errors.comment ? (
                        <p className="text-error text-xs font-medium">{errors.comment.message}</p>
                      ) : (
                        <span />
                      )}
                      <span className={`text-xs tabular-nums ${comment.length >= 171 ? 'text-primary' : 'text-outline'}`}>
                        {comment.length}/191
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <p className="text-label-sm font-semibold text-on-surface mb-2">
                  {t('messengerLabel')}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {/* Telegram/WhatsApp are brand names (identical in every locale);
                      only the phone-call option is translated. The submitted value
                      stays canonical English so the company's inbox is consistent. */}
                  {([
                    { value: 'Telegram', label: 'Telegram' },
                    { value: 'WhatsApp', label: 'WhatsApp' },
                    { value: 'Phone call', label: tForm('messengerPhoneCall') },
                  ] as const).map(({ value, label }) => (
                    <label key={value} className="cursor-pointer">
                      <input type="radio" {...register('messenger')} value={value} className="sr-only peer" />
                      <span className={`block px-2 py-3 rounded-full border text-center text-label-caps font-headline transition-all sm:px-4 ${
                        selectedMessenger === value
                          ? 'bg-primary border-primary text-background font-bold'
                          : 'border-outline-variant text-on-surface-variant hover:border-primary/50'
                      }`}>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {submitError && (
                <p className="text-error text-sm font-medium text-center">{tForm('submitError')}</p>
              )}
              <Button type="submit" variant="gold" fullWidth size="lg" arrow loading={isSubmitting} className="cta-gold-hover">
                {isSubmitting ? tForm('sending') : t('submitCta')}
              </Button>
              </fieldset>
            </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
}

export default function ContactModal(props: ContactModalProps) {
  // SSR guard: ReactDOM.createPortal requires document
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted || typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <ModalContent {...props} />,
    document.body
  );
}
